import { ModelPreview } from '@/components/project-detail/ModelPreview';
import { CollisionStatusTag } from '@/components/projects/CollisionStatusTag';
import type { ProjectListItem, SortConfig, SortField } from '@/types/project.types';
import { extractModelIdFromUrl, formatDateTime, formatUserName } from '@/utils/formatters';
import { AlertCircle, ArrowDown, ArrowUp, Calendar, User } from 'lucide-react';
import { Tooltip } from 'primereact/tooltip';
import type { FC } from 'react';

interface ProjectsTableProps {
  projects: ProjectListItem[];
  selectedProjectIds: Set<string>;
  onProjectClick: (projectId: string) => void;
  onProjectSelect: (projectId: string, selected: boolean) => void;
  onSelectAll: (selected: boolean) => void;
  onUserClick?: (ownerId: string) => void;
  sortConfig?: SortConfig;
  onSortChange?: (config: SortConfig) => void;
}

export const ProjectsTable: FC<ProjectsTableProps> = ({
  projects,
  selectedProjectIds,
  onProjectClick,
  onProjectSelect,
  onSelectAll,
  onUserClick,
  sortConfig,
  onSortChange,
}) => {
  const allSelected = projects.length > 0 && projects.every((p) => selectedProjectIds.has(p.id));
  const someSelected = selectedProjectIds.size > 0 && !allSelected;

  const handleSort = (field: SortField) => {
    if (!onSortChange || !sortConfig) return;

    const newOrder = sortConfig.field === field && sortConfig.order === 'asc' ? 'desc' : 'asc';
    onSortChange({ field, order: newOrder });
  };

  const getSortIcon = (field: SortField) => {
    if (!sortConfig || sortConfig.field !== field) {
      return null;
    }
    return sortConfig.order === 'asc' ? (
      <ArrowUp size={14} className="inline-block ml-1" />
    ) : (
      <ArrowDown size={14} className="inline-block ml-1" />
    );
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectAll(e.target.checked);
  };

  const handleProjectSelect = (projectId: string, e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    onProjectSelect(projectId, e.target.checked);
  };

  const handleUserClick = (e: React.MouseEvent, ownerId: string) => {
    e.stopPropagation();
    if (onUserClick) {
      onUserClick(ownerId);
    }
  };

  if (projects.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow">
        <div className="p-8 text-center text-gray-500">No projects found</div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(input) => {
                    if (input) input.indeterminate = someSelected;
                  }}
                  onChange={handleSelectAll}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                  onClick={(e) => e.stopPropagation()}
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Preview
              </th>
              <th
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  onSortChange ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                }`}
                onClick={() => onSortChange && handleSort('title')}
                onKeyDown={(e) => {
                  if (onSortChange && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleSort('title');
                  }
                }}
                tabIndex={onSortChange ? 0 : undefined}
                role={onSortChange ? 'button' : undefined}
              >
                Title
                {getSortIcon('title')}
              </th>
              <th
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  onSortChange ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                }`}
                onClick={() => onSortChange && handleSort('part_type')}
                onKeyDown={(e) => {
                  if (onSortChange && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleSort('part_type');
                  }
                }}
                tabIndex={onSortChange ? 0 : undefined}
                role={onSortChange ? 'button' : undefined}
              >
                Part Type
                {getSortIcon('part_type')}
              </th>
              <th
                className={`px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                  onSortChange ? 'cursor-pointer hover:bg-gray-100 select-none' : ''
                }`}
                onClick={() => onSortChange && handleSort('created_at')}
                onKeyDown={(e) => {
                  if (onSortChange && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    handleSort('created_at');
                  }
                }}
                tabIndex={onSortChange ? 0 : undefined}
                role={onSortChange ? 'button' : undefined}
              >
                Created
                {getSortIcon('created_at')}
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Owner
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Models</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Collision
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {projects.map((project) => {
              const previewUrl = project.target_png_url || project.blank_png_url;
              const modelId = extractModelIdFromUrl(previewUrl);
              const userName = formatUserName(project);
              const isSelected = selectedProjectIds.has(project.id);

              return (
                <tr
                  key={project.id}
                  className={`hover:bg-gray-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                  onClick={() => onProjectClick(project.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onProjectClick(project.id);
                    }
                  }}
                  tabIndex={0}
                  role="button"
                >
                  <td className="px-4 py-3">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={(e) => handleProjectSelect(project.id, e)}
                      className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <div className="w-20 h-12 bg-gray-100 rounded overflow-hidden">
                      {modelId ? (
                        <ModelPreview modelId={modelId} alt={`Preview of ${project.title}`} disableGif={true} />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-400">
                          No preview
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {project.apt_generation_error && (
                        <>
                          <Tooltip
                            target={`.project-apt-error-row-${project.id}`}
                            position="bottom"
                            style={{ maxWidth: '24rem' }}
                          />
                          <span
                            className={`project-apt-error-row-${project.id} flex items-center justify-center w-5 h-5 rounded-full bg-red-100 text-red-600 cursor-help flex-shrink-0`}
                            data-pr-tooltip={project.apt_generation_error}
                            aria-label={project.apt_generation_error}
                          >
                            <AlertCircle size={12} />
                          </span>
                        </>
                      )}
                      <div className="text-sm font-medium text-gray-900">{project.title}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {project.part_type ? (
                      <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                        {project.part_type}
                      </span>
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calendar size={14} />
                      <span>{formatDateTime(project.created_at)}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div
                      className={`flex items-center gap-2 text-sm text-gray-500 ${onUserClick ? 'cursor-pointer hover:text-gray-700' : ''}`}
                      onClick={(e) => handleUserClick(e, project.owner_id)}
                      onKeyDown={(e) => {
                        if (onUserClick && (e.key === 'Enter' || e.key === ' ')) {
                          e.preventDefault();
                          handleUserClick(e as unknown as React.MouseEvent, project.owner_id);
                        }
                      }}
                      tabIndex={onUserClick ? 0 : undefined}
                      role={onUserClick ? 'button' : undefined}
                    >
                      <User size={14} />
                      <span className="truncate max-w-[150px]" title={userName}>
                        {userName}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      {project.target_png_url && (
                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">Target</span>
                      )}
                      {project.blank_png_url && (
                        <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">Blank</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {project.collision_simulation_state ? (
                      <CollisionStatusTag state={project.collision_simulation_state} />
                    ) : (
                      <span className="text-sm text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
